import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
	Button,
	Empty,
	Group,
	Pagination,
	SkeletonRect,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { TableColumn } from '@common/components/Table/Table';
import type { DiscountsTableRow } from '@ekhodealer/ekho-common';

import { FloatingActionMenu } from '@common/components/FloatingActionMenu';
import NoDiscounts from '@common/components/icons/NoDiscounts';
import { SelectAllBanner } from '@common/components/SelectAllBanner';
import { Table } from '@common/components/Table/Table';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { paths } from '@config/paths';
import { DiscountTab } from '@config/paths/discountTabs';

import { useExportDiscounts } from '../api/useExportDiscounts';
import { useSearchDiscounts } from '../api/useSearchDiscounts';
import { discountDrawerModeAtom, discountFormDirtyAtom } from '../atoms/discountDetailsDrawerAtoms';
import { DISCOUNT_FORM_DEFAULTS, discountFormAtom } from '../atoms/discountFormAtoms';
import {
	currentPageAtom,
	debouncedSearchValueAtom,
	discountsParamsAtom,
	discountTabFilterAtom,
	isAllMatchingDiscountsSelectedAtom,
	itemsPerPageAtom,
	selectedDiscountsAtom,
	updateItemsPerPageAtom,
	updateSelectedDiscountsAtom,
} from '../atoms/discountsAtoms';
import { useDiscountNavigation } from '../hooks/useDiscountNavigation';
import { currentDiscountsListAtom } from '../hooks/useDiscountNavigation';

import { DiscountActionMenu } from './DiscountActionMenu';
import {
	AppliesToCell,
	// CombinationsCell,
	MethodCell,
	NameCell,
	StatusCell,
	// TypeCell,
	// UsedCell,
} from './table-cells';

// Enum for column IDs
enum DiscountsTableColumnId {
	NAME = 'name',
	CODE = 'code',
	STATUS = 'status',
	METHOD = 'method',
	TYPE = 'type',
	APPLIES_TO = 'appliesTo',
	COMBINATIONS = 'combinations',
	USED = 'used',
	ACTIONS = 'actions',
}

// Centralized cell rendering function
const renderCell = (
	columnId: DiscountsTableColumnId,
	item: DiscountsTableRow,
	isLoading: boolean,
): React.ReactNode => {
	if (isLoading) {
		return <SkeletonRect width={100} height={24} />;
	}

	switch (columnId) {
		case DiscountsTableColumnId.NAME:
			return <NameCell name={item.header} />;
		case DiscountsTableColumnId.STATUS:
			return <StatusCell status={item.status} />;
		case DiscountsTableColumnId.METHOD:
			return <MethodCell method={item.method} />;
		// case DiscountsTableColumnId.TYPE:
		// 	return <TypeCell type={item.type} />;
		case DiscountsTableColumnId.APPLIES_TO:
			return <AppliesToCell appliesTo={item.appliesTo} />;
		// case DiscountsTableColumnId.COMBINATIONS:
		// 	return <CombinationsCell combinations={item.combinations} />;
		// case DiscountsTableColumnId.USED:
		// 	return <UsedCell used={item.used} />;
		case DiscountsTableColumnId.ACTIONS:
			return <DiscountActionMenu item={item} />;
		default:
			return <Text size='bodySmall'>—</Text>;
	}
};

export const DiscountsTable = () => {
	const navigate = useNavigate();
	const servicesProviderId = useCurrentServicesProviderId();

	// Get filter params from atoms
	const params = useAtomValue(discountsParamsAtom);

	// Pagination state
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const updateItemsPerPage = useSetAtom(updateItemsPerPageAtom);

	// Selection state
	const selectedDiscounts = useAtomValue(selectedDiscountsAtom);
	const updateSelectedDiscounts = useSetAtom(updateSelectedDiscountsAtom);
	const [isAllMatchingSelected, setIsAllMatchingSelected] = useAtom(
		isAllMatchingDiscountsSelectedAtom,
	);

	// Navigation and drawer mode
	const { openDiscountDetails, openCreateDiscount } = useDiscountNavigation();
	const setDrawerMode = useSetAtom(discountDrawerModeAtom);
	const setDiscountForm = useSetAtom(discountFormAtom);
	const setFormDirty = useSetAtom(discountFormDirtyAtom);

	// React query hook to get discounts data
	const { data, isLoading } = useSearchDiscounts(params);
	const exportMutation = useExportDiscounts();
	const items = useMemo(() => data?.data ?? [], [data?.data]);
	const queryCount = data?.count ?? 0;

	// Sync current discounts list for navigation
	const updateCurrentDiscountsList = useSetAtom(currentDiscountsListAtom);
	useEffect(() => {
		if (!isLoading && items.length > 0) {
			updateCurrentDiscountsList(items.map((item) => item.id));
		}
	}, [items, isLoading, updateCurrentDiscountsList]);

	const isAllPageSelected = selectedDiscounts === 'all';
	const selectedDiscountsCount = isAllMatchingSelected
		? queryCount
		: isAllPageSelected
			? items.length
			: (selectedDiscounts as Set<string>).size;

	// Check if we have empty results (not loading, no data)
	const hasEmptyResults = !isLoading && items.length === 0;

	// Detect active search/tab filters
	const searchValue = useAtomValue(debouncedSearchValueAtom);
	const tabFilter = useAtomValue(discountTabFilterAtom);
	const setSearchValue = useSetAtom(debouncedSearchValueAtom);
	const setTabFilter = useSetAtom(discountTabFilterAtom);
	const hasFiltersApplied =
		searchValue.trim().length > 0 || (tabFilter !== null && tabFilter !== DiscountTab.ALL);

	const clearAllFilters = () => {
		setSearchValue('');
		setTabFilter(DiscountTab.ALL);
		setCurrentPage(1);
		if (servicesProviderId) {
			navigate(paths.discountsTab.getHref(servicesProviderId, DiscountTab.ALL));
		}
	};

	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		updateItemsPerPage(newItemsPerPage);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleRowActivate = (item: DiscountsTableRow) => {
		setDrawerMode('view');
		openDiscountDetails(item.id);
	};

	// Placeholder for skeleton items when loading
	const getDisplayItems = () => {
		if (isLoading) {
			return Array.from({ length: itemsPerPage }, (_, index) => ({
				id: `skeleton-${index}`,
				header: '',
				status: '',
				method: '',
				appliesTo: [],
			})) as unknown as DiscountsTableRow[];
		}

		return items;
	};

	const handleCreateDiscount = () => {
		setDrawerMode('create');
		setDiscountForm(DISCOUNT_FORM_DEFAULTS);
		setFormDirty(false);
		openCreateDiscount();
	};

	// Show empty state when there are no results
	if (hasEmptyResults) {
		return (
			<Empty
				icon={<NoDiscounts />}
				title={hasFiltersApplied ? 'No discounts match your filters' : 'No discounts yet'}
				description={
					hasFiltersApplied
						? 'Try adjusting or clearing your filters to see more results.'
						: 'Create your first discount to start offering promotions to customers.'
				}
				content={
					hasFiltersApplied ? (
						<Button variant='secondary' size='small' onPress={() => clearAllFilters()}>
							Clear all
						</Button>
					) : (
						<Button variant='filled' size='small' onPress={handleCreateDiscount}>
							Create discount
						</Button>
					)
				}
			/>
		);
	}

	const columns: TableColumn<DiscountsTableRow>[] = [
		{
			id: DiscountsTableColumnId.NAME,
			title: 'Name',
			sortable: false,
			sortDirection: 'none',
			onSort: () => {},
			renderCell: (item) => renderCell(DiscountsTableColumnId.NAME, item, isLoading),
		},
		{
			id: DiscountsTableColumnId.STATUS,
			title: 'Status',
			sortable: false,
			sortDirection: 'none',
			onSort: () => {},
			isButtonCell: true,
			renderCell: (item) => renderCell(DiscountsTableColumnId.STATUS, item, isLoading),
		},
		{
			id: DiscountsTableColumnId.METHOD,
			title: 'Method',
			sortable: false,
			sortDirection: 'none',
			onSort: () => {},
			renderCell: (item) => renderCell(DiscountsTableColumnId.METHOD, item, isLoading),
		},
		// {
		// 	id: DiscountsTableColumnId.TYPE,
		// 	title: 'Type',
		// 	sortable: false,
		// 	sortDirection: 'none',
		// 	onSort: () => {},
		// 	renderCell: (item) => renderCell(DiscountsTableColumnId.TYPE, item, isLoading),
		// },
		{
			id: DiscountsTableColumnId.APPLIES_TO,
			title: 'Applies to',
			sortable: false,
			sortDirection: 'none',
			onSort: () => {},
			isButtonCell: true,
			renderCell: (item) => renderCell(DiscountsTableColumnId.APPLIES_TO, item, isLoading),
		},
		// {
		// 	id: DiscountsTableColumnId.COMBINATIONS,
		// 	title: 'Combinations',
		// 	sortable: false,
		// 	sortDirection: 'none',
		// 	onSort: () => {},
		// 	renderCell: (item) => renderCell(DiscountsTableColumnId.COMBINATIONS, item, isLoading),
		// },
		// {
		// 	id: DiscountsTableColumnId.USED,
		// 	title: 'Used',
		// 	sortable: false,
		// 	sortDirection: 'none',
		// 	onSort: () => {},
		// 	renderCell: (item) => renderCell(DiscountsTableColumnId.USED, item, isLoading),
		// },
		{
			id: DiscountsTableColumnId.ACTIONS,
			title: '',
			sortable: false,
			renderCell: (item) => renderCell(DiscountsTableColumnId.ACTIONS, item, isLoading),
			renderHeader: () => null, // No header content
		},
	];

	return (
		<>
			<Table<DiscountsTableRow>
				columns={columns}
				items={getDisplayItems()}
				getItemId={(item) => item.id}
				selectionMode='multiple'
				selectedItems={selectedDiscounts}
				onSelectionChange={updateSelectedDiscounts}
				onRowActivate={handleRowActivate}
				aria-label={isLoading ? 'Loading discounts table' : 'Discounts table'}
				stickyRightCount={1}
				belowTableContent={
					!hasEmptyResults &&
					!isLoading && (
						<Pagination
							variant='contained'
							currentPage={currentPage}
							itemsPerPage={itemsPerPage}
							totalItems={queryCount}
							itemsPerPageOptions={[10, 25, 50, 100]}
							onNavigateTo={handlePageChange}
							onItemsPerPageChange={handleItemsPerPageChange}
							aria-label='Discounts table pagination'
						/>
					)
				}
			/>
			<FloatingActionMenu
				isOpen={selectedDiscountsCount > 0}
				onClose={() => {
					updateSelectedDiscounts(new Set());
				}}
				closeButtonTooltip='Deselect all'
				leftContent={
					<div style={{ marginRight: 'auto' }}>
						<SelectAllBanner
							selectedCount={
								isAllPageSelected
									? items.length
									: (selectedDiscounts as Set<string>).size
							}
							totalCount={queryCount}
							isAllPageSelected={isAllPageSelected}
							isAllMatchingSelected={isAllMatchingSelected}
							onSelectAllMatching={() => setIsAllMatchingSelected(true)}
							onClearSelection={() => updateSelectedDiscounts(new Set())}
							resourceName='discounts'
						/>
					</div>
				}
				rightContent={
					<Group noWrap itemsSpacing='8'>
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							isDisabled={exportMutation.isPending}
							onPress={async () => {
								const ids = isAllMatchingSelected
									? undefined
									: selectedDiscounts === 'all'
										? items.map((item) => item.id)
										: Array.from(selectedDiscounts as Set<string>);
								await exportMutation.mutateAsync({ discountIds: ids });
								toast({
									message: 'Discounts exported successfully',
									variant: 'success',
								});
								updateSelectedDiscounts(new Set());
							}}>
							{exportMutation.isPending ? 'Exporting...' : 'Export'}
						</Button>
					</Group>
				}
			/>
		</>
	);
};
